import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Typography, Box, Chip, IconButton, List, ListItem, ListItemText,
  ListItemIcon, ListItemSecondaryAction, Badge, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Grid, Divider, Avatar, Tooltip, CircularProgress, Alert, Snackbar, useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  SystemUpdate as SystemUpdateIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Article as ArticleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import notificationApi, { Notification, NotificationPreferences } from '../../services/notificationApi';

interface NotificationCenterProps {}

interface NotificationItem extends Notification {
  priority: 'high' | 'medium' | 'low';
  icon: React.ComponentType<any>;
  color: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = () => {
  const theme = useTheme();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [settings, setSettings] = useState<NotificationPreferences>({
    emailNotifications: true,
    pushNotifications: true,
    prUpdates: true,
    paymentAlerts: true,
    campaignUpdates: true,
    systemAlerts: true,
  });
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Map notification type to icon, color, and priority (using website colors)
  const getNotificationMetadata = (type: string): { icon: React.ComponentType<any>, color: string, priority: 'high' | 'medium' | 'low' } => {
    switch (type) {
      case 'pr_published':
      case 'pr_verified':
      case 'pr_distributed':
        return { icon: CheckCircleIcon, color: '#4caf50', priority: 'medium' };
      case 'pr_rejected':
      case 'verification_rejected':
      case 'payment_failed':
        return { icon: ErrorIcon, color: '#f44336', priority: 'high' };
      case 'payment':
      case 'payment_processed':
        return { icon: PaymentIcon, color: '#4caf50', priority: 'medium' };
      case 'campaign':
      case 'campaign_started':
      case 'campaign_milestone':
        return { icon: PublicIcon, color: '#0927eb', priority: 'low' };
      case 'system':
        return { icon: SystemUpdateIcon, color: '#ff5200', priority: 'medium' };
      case 'success':
        return { icon: CheckCircleIcon, color: '#4caf50', priority: 'low' };
      case 'warning':
        return { icon: WarningIcon, color: '#ff5200', priority: 'high' };
      case 'info':
        return { icon: InfoIcon, color: '#0927eb', priority: 'low' };
      case 'pr_submitted':
        return { icon: ArticleIcon, color: '#0927eb', priority: 'low' };
      case 'verification_approved':
        return { icon: CheckCircleIcon, color: '#4caf50', priority: 'medium' };
      default:
        return { icon: NotificationsIcon, color: '#cdcdcd', priority: 'low' };
    }
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await notificationApi.getUserNotifications(1, 100, false);
      
      if (response.success && response.notifications) {
        // Transform notifications to include UI metadata
        const transformedNotifications: NotificationItem[] = response.notifications.map((notif: Notification) => {
          const metadata = getNotificationMetadata(notif.type);
          return {
            ...notif,
            ...metadata,
          };
        });
        setNotifications(transformedNotifications);
      }
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch notification preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await notificationApi.getPreferences();
      if (response.success && response.preferences) {
        setSettings(response.preferences);
      }
    } catch (err: any) {
      console.error('Failed to fetch preferences:', err);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
  }, [fetchNotifications, fetchPreferences]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#f44336'; // Error red
      case 'medium': return '#ff5200'; // Secondary orange
      case 'low': return '#0927eb'; // Primary blue
      default: return '#cdcdcd'; // Gray
    }
  };

  const formatTimestamp = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const response = await notificationApi.markAsRead(id);
      if (response.success) {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
        setSuccessMessage('Notification marked as read');
      }
    } catch (err: any) {
      console.error('Failed to mark as read:', err);
      setError(err.message || 'Failed to mark notification as read');
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const response = await notificationApi.deleteNotification(id);
      if (response.success) {
    setNotifications(notifications.filter(n => n.id !== id));
        setSuccessMessage('Notification deleted');
      }
    } catch (err: any) {
      console.error('Failed to delete notification:', err);
      setError(err.message || 'Failed to delete notification');
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await notificationApi.markAllAsRead();
      if (response.success) {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
        setSuccessMessage(`Marked ${response.updatedCount || notifications.length} notifications as read`);
      }
    } catch (err: any) {
      console.error('Failed to mark all as read:', err);
      setError(err.message || 'Failed to mark all notifications as read');
    }
  };

  const saveSettings = async () => {
    try {
      const response = await notificationApi.updatePreferences(settings);
      if (response.success) {
        setShowSettings(false);
        setSuccessMessage('Settings saved successfully');
      }
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.message || 'Failed to save settings');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const highPriorityUnread = notifications.filter(n => n.priority === 'high' && !n.read).length;

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress style={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Typography variant="h4" style={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
          Notification Center
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" style={{ marginLeft: '15px' }}>
              <NotificationsIcon style={{ color: theme.palette.primary.main }} />
            </Badge>
          )}
        </Typography>
        <Box>
          <IconButton
            onClick={() => fetchNotifications(true)}
            disabled={refreshing}
            style={{ color: theme.palette.text.primary, marginRight: '10px' }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<MarkEmailReadIcon />}
            style={{ backgroundColor: theme.palette.text.primary, color: theme.palette.background.paper, marginRight: '10px' }}
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark All Read
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
            onClick={() => setShowSettings(true)}
          >
            Settings
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} style={{ marginBottom: '20px' }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Notifications List */}
        <Grid item xs={12} md={8}>
          <Card style={{ 
            borderRadius: '16px', 
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2]
          }}>
            <CardContent>
              <Typography variant="h6" style={{ color: theme.palette.text.primary, marginBottom: '15px', fontWeight: 'bold' }}>
                Recent Notifications
              </Typography>
              {notifications.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Typography style={{ color: theme.palette.text.secondary }}>
                    No notifications available
                  </Typography>
                </Box>
              ) : (
              <List>
                {notifications.map((notification) => {
                    const IconComponent = notification.icon;
                  return (
                    <ListItem
                      key={notification.id}
                      style={{
                          backgroundColor: notification.read 
                            ? 'transparent' 
                            : theme.palette.mode === 'dark' 
                              ? 'rgba(9, 39, 235, 0.15)' 
                              : 'rgba(9, 39, 235, 0.05)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                          border: notification.read 
                            ? `1px solid ${theme.palette.divider}` 
                            : `1px solid ${theme.palette.primary.main}40`
                      }}
                    >
                      <ListItemIcon>
                        <Avatar style={{ backgroundColor: notification.color, width: '40px', height: '40px' }}>
                          <IconComponent style={{ color: 'white' }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <Typography style={{ 
                                color: theme.palette.text.primary, 
                                fontWeight: notification.read ? 'normal' : 'bold' 
                              }}>
                              {notification.title}
                            </Typography>
                            <Chip
                              label={notification.priority}
                              size="small"
                              style={{
                                backgroundColor: getPriorityColor(notification.priority),
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '10px'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                              <Typography style={{ 
                                color: theme.palette.text.secondary, 
                                marginBottom: '5px' 
                              }}>
                              {notification.message}
                            </Typography>
                              <Typography style={{ 
                                color: theme.palette.text.secondary, 
                                fontSize: '12px',
                                opacity: 0.7
                              }}>
                                {formatTimestamp(notification.timestamp || notification.createdAt || new Date())}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box style={{ display: 'flex', gap: '5px' }}>
                          {!notification.read && (
                            <Tooltip title="Mark as Read">
                              <IconButton
                                size="small"
                                  style={{ color: theme.palette.primary.main }}
                                onClick={() => markAsRead(notification.id)}
                              >
                                <MarkEmailReadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                                style={{ color: theme.palette.error.main }}
                              onClick={() => deleteNotification(notification.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Stats */}
        <Grid item xs={12} md={4}>
          <Card style={{ 
            borderRadius: '16px', 
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2],
            marginBottom: '20px'
          }}>
            <CardContent>
              <Typography variant="h6" style={{ color: theme.palette.text.primary, marginBottom: '15px', fontWeight: 'bold' }}>
                Notification Stats
              </Typography>
              <Box style={{ marginBottom: '15px' }}>
                <Typography variant="body1" style={{ color: theme.palette.text.secondary }}>Total Notifications:</Typography>
                <Typography variant="h4" style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                  {notifications.length}
                </Typography>
              </Box>
              <Box style={{ marginBottom: '15px' }}>
                <Typography variant="body1" style={{ color: theme.palette.text.secondary }}>Unread:</Typography>
                <Typography variant="h4" style={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
                  {unreadCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" style={{ color: theme.palette.text.secondary }}>High Priority:</Typography>
                <Typography variant="h4" style={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
                  {highPriorityUnread}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card style={{ 
            borderRadius: '16px', 
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2]
          }}>
            <CardContent>
              <Typography variant="h6" style={{ color: theme.palette.text.primary, marginBottom: '15px', fontWeight: 'bold' }}>
                Quick Actions
              </Typography>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                  onClick={() => setShowSettings(true)}
                >
                  Email Settings
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SecurityIcon />}
                  style={{ backgroundColor: theme.palette.error.main, color: 'white' }}
                  onClick={() => setShowSettings(true)}
                >
                  Security Alerts
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SystemUpdateIcon />}
                  style={{ backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText }}
                  onClick={() => setShowSettings(true)}
                >
                  System Updates
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
          Notification Settings
        </DialogTitle>
        <DialogContent style={{ backgroundColor: theme.palette.background.paper, padding: '20px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" style={{ marginBottom: '15px', color: theme.palette.primary.main, fontWeight: 'bold' }}>
                Notification Preferences
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                  />
                }
                label="Email Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.pushNotifications}
                    onChange={(e) => setSettings({...settings, pushNotifications: e.target.checked})}
                  />
                }
                label="Push Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.prUpdates}
                    onChange={(e) => setSettings({...settings, prUpdates: e.target.checked})}
                  />
                }
                label="PR Updates"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.paymentAlerts}
                    onChange={(e) => setSettings({...settings, paymentAlerts: e.target.checked})}
                  />
                }
                label="Payment Alerts"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.campaignUpdates}
                    onChange={(e) => setSettings({...settings, campaignUpdates: e.target.checked})}
                  />
                }
                label="Campaign Updates"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.systemAlerts}
                    onChange={(e) => setSettings({...settings, systemAlerts: e.target.checked})}
                  />
                }
                label="System Alerts"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ backgroundColor: theme.palette.background.paper, padding: '20px' }}>
          <Button onClick={() => setShowSettings(false)} style={{ color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }} 
            onClick={saveSettings}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationCenter;
